import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useXmpCoverPipeline } from './hooks/useXmpCoverPipeline';
import PipelineFileList from './components/PipelineFileList';
import { BATCH_WARNING_THRESHOLD_BYTES } from './utils/pipelineFileUtils';

function DropZone({ onFilesSelect }) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const dropped = Array.from(e.dataTransfer.files).filter((f) =>
      f.name.toLowerCase().endsWith('.pdf')
    );
    if (dropped.length) onFilesSelect(dropped);
  }, [onFilesSelect]);

  const handleChange = useCallback((e) => {
    const selected = Array.from(e.target.files);
    if (selected.length) onFilesSelect(selected);
    e.target.value = '';
  }, [onFilesSelect]);

  return (
    <Paper
      sx={{
        p: 6,
        mb: 3,
        border: dragActive ? '3px dashed #1976d2' : '2px dashed #ccc',
        backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        textAlign: 'center',
      }}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <CloudUploadIcon sx={{ fontSize: 48, color: dragActive ? 'primary.main' : 'text.secondary' }} />
        <Typography variant="h5" color={dragActive ? 'primary.main' : 'text.primary'}>
          {dragActive ? 'Drop PDFs here' : 'Drag & Drop PDFs'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          or click to select — filenames must contain an EAN-13 or DAC key
        </Typography>
      </Box>
    </Paper>
  );
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function XmpCoverPipeline() {
  const { files, addFiles, retry, setStageSelected } = useXmpCoverPipeline();

  const totalBytes = useMemo(
    () => files.reduce((sum, entry) => sum + entry.file.size, 0),
    [files]
  );
  const showBatchWarning = totalBytes > BATCH_WARNING_THRESHOLD_BYTES;

  const handleDownloadCover = (entry) => {
    const blob = entry.stages.cover.resultBlob;
    if (blob) downloadBlob(entry.filename, blob);
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom>
        XMP + Cover Pipeline
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload PDFs to run XMP metadata injection and cover binding in sequence. Both run by
        default — uncheck a stage per file if it isn't needed, then use Retry to re-run with the
        current selection.
      </Typography>

      <DropZone onFilesSelect={addFiles} />

      {showBatchWarning && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This batch totals over 1GB — uploads may take a while depending on your connection.
        </Alert>
      )}

      <PipelineFileList
        files={files}
        onDownloadCover={handleDownloadCover}
        onToggleStage={(entry, stageName, checked) => setStageSelected(entry.id, stageName, checked)}
        onRetry={retry}
      />
    </Box>
  );
}

export default XmpCoverPipeline;
